import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
    Mail, Search, RefreshCw, Eye, Shield, 
    QrCode, Copy, Check, Printer, X, Image as ImageIcon, CheckCircle2,
    Plus, MessageSquare, ExternalLink, Package, Clock, Camera, Link2, ArrowRight,
    UserCheck, Building2, Lock
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { 
    fetchCorrespondencias, 
    Correspondencia,
    fetchMiAccesoCorrespondencia,
    MiAccesoCorrespondencia
} from '../../../../services/correspondencia';
import RegistrarEncomiendaClienteModal from './RegistrarEncomiendaClienteModal';
import Pagination from '../../../../components/Pagination/Pagination';
import { useTheme } from '../../../../context/ThemeContext';


const MiCorrespondenciaPage: React.FC = () => {
    const { isDark } = useTheme();
    const [correspondencias, setCorrespondencias] = useState<Correspondencia[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [busqueda, setBusqueda] = useState('');
    const [filtroEstado, setFiltroEstado] = useState<string>('todos');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(5);

    const [verFotoModal, setVerFotoModal] = useState<string | null>(null);
    const [comprobanteQR, setComprobanteQR] = useState<Correspondencia | null>(null);
    const [procesoEnvioItem, setProcesoEnvioItem] = useState<Correspondencia | null>(null);
    const [copiadoPinId, setCopiadoPinId] = useState<number | null>(null);
    const [copiadoCodigoId, setCopiadoCodigoId] = useState<number | null>(null);
    const [copiadoPublicLink, setCopiadoPublicLink] = useState(false);
    const [mostrarModalRegistrar, setMostrarModalRegistrar] = useState(false);

    const [accesoInfo, setAccesoInfo] = useState<MiAccesoCorrespondencia | null>(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [dataCorrespondencias, dataAcceso] = await Promise.all([
                fetchCorrespondencias().catch(() => []),
                fetchMiAccesoCorrespondencia().catch(() => ({
                    tiene_acceso: false,
                    is_staff_or_admin: false,
                    es_domiciliado: false,
                    tiene_servicio_correspondencia: false
                }))
            ]);
            setCorrespondencias(dataCorrespondencias);
            setAccesoInfo(dataAcceso);
        } catch (err) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const formatDateTime = (dateString: string | null): string => {
        if (!dateString) return 'Pendiente';
        const date = new Date(dateString);
        return date.toLocaleString('es-BO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    const getEstadoColor = (estado: string) => {
        switch (estado) {
            case 'recibido': return isDark ? 'bg-purple-900/40 text-[#ffd97c] border-purple-500/40' : 'bg-purple-100 text-purple-800 border-purple-200';
            case 'notificado': return isDark ? 'bg-amber-900/40 text-amber-300 border-amber-500/40' : 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'entregado': return isDark ? 'bg-emerald-900/40 text-emerald-300 border-emerald-500/40' : 'bg-green-100 text-green-800 border-green-200';
            case 'cancelado': return isDark ? 'bg-red-900/40 text-red-300 border-red-500/40' : 'bg-red-100 text-red-800 border-red-200';
            default: return isDark ? 'bg-gray-800 text-gray-300 border-gray-700' : 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getEstadoTexto = (estado: string) => {
        switch (estado) {
            case 'recibido': return 'En Recepción';
            case 'notificado': return 'Listo para Retiro';
            case 'entregado': return 'Entregado';
            case 'cancelado': return 'Cancelado';
            default: return estado;
        }
    };

    const handleCopiarPin = (id: number, pin: string) => {
        navigator.clipboard.writeText(pin);
        setCopiadoPinId(id);
        setTimeout(() => setCopiadoPinId(null), 2000);
    };

    const handleCopiarCodigo = (id: number, codigo: string) => {
        navigator.clipboard.writeText(codigo);
        setCopiadoCodigoId(id);
        setTimeout(() => setCopiadoCodigoId(null), 2000);
    };

    const handleImprimirComprobante = () => {
        window.print();
    };

    const correspondenciasFiltradas = correspondencias.filter(item => {
        if (filtroEstado !== 'todos' && item.estado !== filtroEstado) {
            return false;
        }

        if (busqueda.trim()) {
            const term = busqueda.toLowerCase();
            return (
                (item.codigo && item.codigo.toLowerCase().includes(term)) ||
                (item.cita_codigo && item.cita_codigo.toLowerCase().includes(term)) ||
                (item.descripcion && item.descripcion.toLowerCase().includes(term)) ||
                item.remitente_nombre.toLowerCase().includes(term) ||
                item.remitente_apellido.toLowerCase().includes(term) ||
                item.remitente_ci_nit.toLowerCase().includes(term)
            );
        }

        return true;
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [busqueda, filtroEstado]);

    const totalPages = Math.ceil(correspondenciasFiltradas.length / itemsPerPage) || 1;
    const paginatedItems = correspondenciasFiltradas.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const totalRecibidas = correspondencias.filter(c => c.estado === 'recibido').length;
    const totalNotificadas = correspondencias.filter(c => c.estado === 'notificado').length;
    const totalEntregadas = correspondencias.filter(c => c.estado === 'entregado').length;

    // Si el usuario no tiene rol ni servicio activo con correspondencia
    if (!loading && accesoInfo && !accesoInfo.tiene_acceso) {
        return (
            <div className="max-w-4xl mx-auto p-4 md:p-12 text-center space-y-6 animate-fade-in font-['DM_Sans',sans-serif]">
                <div 
                    className="p-8 md:p-12 rounded-3xl border shadow-xl space-y-6"
                    style={{
                        background: isDark ? 'rgba(26, 11, 46, 0.85)' : '#ffffff',
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb',
                        color: isDark ? '#ffffff' : '#0f172a'
                    }}
                >
                    <div className="w-20 h-20 bg-gradient-to-tr from-[#61257B] to-[#C1348B] rounded-3xl mx-auto flex items-center justify-center text-white shadow-lg shadow-purple-900/30">
                        <Lock className="w-10 h-10" />
                    </div>
                    <div className="space-y-2">
                        <span className="px-3 py-1 bg-amber-500/10 text-amber-500 text-xs font-bold uppercase tracking-wider rounded-full border border-amber-500/20 inline-block">
                            Acceso Exclusivo
                        </span>
                        <h2 className="text-2xl md:text-3xl font-black tracking-tight" style={{ color: isDark ? '#ffffff' : '#111827' }}>
                            Recepción y Custodia de Correspondencia
                        </h2>
                        <p className="text-sm max-w-lg mx-auto leading-relaxed" style={{ color: isDark ? '#cbd5e1' : '#4b5563' }}>
                            Este módulo está disponible exclusivamente para usuarios con el rol de <strong>Domiciliado</strong> o con un <strong>Servicio activo con correspondencia habilitada</strong>.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl mx-auto text-left pt-2">
                        <div 
                            className="p-4 rounded-2xl border"
                            style={{
                                background: isDark ? 'rgba(97, 37, 123, 0.15)' : 'rgba(243, 232, 255, 0.7)',
                                borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#e9d5ff'
                            }}
                        >
                            <div className="flex items-center gap-2 font-bold text-sm mb-1" style={{ color: isDark ? '#ffd97c' : '#581c87' }}>
                                <Building2 className="w-4 h-4 text-[#C1348B]" />
                                Plan Domiciliado (Oficina Virtual)
                            </div>
                            <p className="text-xs" style={{ color: isDark ? '#e2e8f0' : '#6b21a8' }}>
                                Domicilia tu empresa, recibe correspondencia ilimitada con seguimiento QR y obtén 8h de salas de regalo al mes.
                            </p>
                        </div>

                        <div 
                            className="p-4 rounded-2xl border"
                            style={{
                                background: isDark ? 'rgba(193, 52, 139, 0.12)' : 'rgba(239, 246, 255, 0.7)',
                                borderColor: isDark ? 'rgba(193, 52, 139, 0.25)' : '#bfdbfe'
                            }}
                        >
                            <div className="flex items-center gap-2 font-bold text-sm mb-1" style={{ color: isDark ? '#ffd97c' : '#1e3a8a' }}>
                                <Package className="w-4 h-4 text-[#C1348B]" />
                                Servicio con Correspondencia
                            </div>
                            <p className="text-xs" style={{ color: isDark ? '#e2e8f0' : '#1e40af' }}>
                                Contrata un servicio de coworking o sala que incluya recepción y custodia de encomiendas con PIN secreto.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                        <a
                            href="/reservaya"
                            className="px-6 py-3 bg-gradient-to-r from-[#61257B] to-[#C1348B] text-white font-bold text-xs rounded-xl shadow-md hover:shadow-lg shadow-purple-900/30 hover:opacity-95 transition-all"
                        >
                            Ver Servicios y Reservas
                        </a>
                        <a
                            href="/midashboard"
                            className="px-6 py-3 font-bold text-xs rounded-xl transition-all"
                            style={{
                                background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                                color: isDark ? '#ffffff' : '#374151'
                            }}
                        >
                            Ir a Mi Dashboard
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6 animate-fade-in font-['DM_Sans',sans-serif]">
            <style>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    #print-comprobante-qr, #print-comprobante-qr * {
                        visibility: visible;
                    }
                    #print-comprobante-qr {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                }
            `}</style>

            {/* Modal de Foto Ampliada */}
            {verFotoModal && createPortal(
                <div 
                    className="fixed inset-0 bg-black/85 flex items-center justify-center z-[9999] p-4 cursor-pointer backdrop-blur-md"
                    onClick={() => setVerFotoModal(null)}
                >
                    <div className="relative max-w-3xl max-h-[85vh] bg-white rounded-2xl overflow-hidden shadow-2xl p-2" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setVerFotoModal(null)}
                            className="absolute top-4 right-4 p-2 bg-black/60 text-white rounded-full hover:bg-black transition-colors z-10"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <img src={verFotoModal} alt="Foto Ampliada" className="max-w-full max-h-[80vh] object-contain rounded-xl" />
                    </div>
                </div>,
                document.body
            )}

            {/* Modal de Comprobante QR para Retiro */}
            {comprobanteQR && createPortal(
                <div 
                    className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
                    onClick={() => setComprobanteQR(null)}
                >
                    <div 
                        className="rounded-3xl shadow-2xl max-w-md w-full max-h-[92vh] flex flex-col overflow-hidden animate-scale-up my-auto border" 
                        style={{
                            background: isDark ? '#150a26' : '#ffffff',
                            borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#e5e7eb',
                            color: isDark ? '#ffffff' : '#111827'
                        }}
                        id="print-comprobante-qr"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header estático siempre visible con botón X */}
                        <div className="sticky top-0 bg-gradient-to-r from-[#61257B] to-[#C1348B] p-5 text-white text-center relative flex-shrink-0 z-10">
                            <button
                                onClick={() => setComprobanteQR(null)}
                                className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                                title="Cerrar comprobante"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <Shield className="w-9 h-9 mx-auto mb-1.5 text-white/90" />
                            <h3 className="text-xl font-bold">Comprobante de Retiro</h3>
                            <p className="text-xs text-white/80 mt-0.5">Presenta este comprobante en recepción</p>
                        </div>

                        {/* Contenido scrolleable con scroll suave que permite subir y bajar sin trabarse */}
                        <div className="p-5 sm:p-6 space-y-4 text-center overflow-y-auto flex-1 custom-scroll">
                            <div 
                                className="p-3 rounded-2xl text-xs font-medium text-left border"
                                style={{
                                    background: isDark ? 'rgba(16, 185, 129, 0.15)' : '#ecfdf5',
                                    borderColor: isDark ? 'rgba(16, 185, 129, 0.3)' : '#a7f3d0',
                                    color: isDark ? '#a7f3d0' : '#065f46'
                                }}
                            >
                                💡 <strong>3 Opciones para recoger:</strong> Muestra el <strong>QR</strong> a la cámara, o proporciona tu <strong>Código</strong> o <strong>PIN</strong> al recepcionista.
                            </div>

                            {/* Opción 1: Código de Retiro */}
                            <div 
                                className="rounded-2xl p-4 text-left flex items-center justify-between border"
                                style={{
                                    background: isDark ? 'rgba(97, 37, 123, 0.25)' : 'rgba(245, 243, 255, 0.8)',
                                    borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#ddd6fe'
                                }}
                            >
                                <div>
                                    <span className="text-[11px] font-bold uppercase tracking-wider block" style={{ color: isDark ? '#ffd97c' : '#61257B' }}>Código de Retiro</span>
                                    <p className="text-xl font-mono font-black tracking-wider" style={{ color: isDark ? '#ffffff' : '#111827' }}>
                                        {comprobanteQR.codigo || `#${comprobanteQR.id}`}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleCopiarCodigo(comprobanteQR.id, comprobanteQR.codigo || `#${comprobanteQR.id}`)}
                                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-xl shadow-sm border transition-all"
                                    style={{
                                        color: isDark ? '#ffd97c' : '#61257B',
                                        background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                                        borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#ddd6fe'
                                    }}
                                >
                                    {copiadoCodigoId === comprobanteQR.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                    <span>{copiadoCodigoId === comprobanteQR.id ? '¡Copiado!' : 'Copiar Código'}</span>
                                </button>
                            </div>

                            {/* Opción 2: PIN Secreto */}
                            {comprobanteQR.pin_seguridad && (
                                <div 
                                    className="rounded-2xl p-4 text-left flex items-center justify-between border"
                                    style={{
                                        background: isDark ? 'rgba(234, 132, 6, 0.15)' : 'rgba(254, 243, 199, 0.8)',
                                        borderColor: isDark ? 'rgba(234, 132, 6, 0.3)' : '#fde68a'
                                    }}
                                >
                                    <div>
                                        <span className="text-[11px] font-bold uppercase tracking-wider block" style={{ color: isDark ? '#ffd97c' : '#b45309' }}>PIN Secreto de Retiro</span>
                                        <p className="text-2xl font-mono font-black tracking-widest" style={{ color: isDark ? '#ffd97c' : '#78350f' }}>
                                            {comprobanteQR.pin_seguridad}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleCopiarPin(comprobanteQR.id, comprobanteQR.pin_seguridad || '')}
                                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-xl shadow-sm border transition-all"
                                        style={{
                                            color: isDark ? '#ffd97c' : '#92400e',
                                            background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                                            borderColor: isDark ? 'rgba(234, 132, 6, 0.3)' : '#fde68a'
                                        }}
                                    >
                                        {copiadoPinId === comprobanteQR.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                        <span>{copiadoPinId === comprobanteQR.id ? '¡Copiado!' : 'Copiar PIN'}</span>
                                    </button>
                                </div>
                            )}

                            {/* Opción 3: Código QR */}
                            <div 
                                className="flex flex-col items-center p-4 rounded-2xl border"
                                style={{
                                    background: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f9fafb',
                                    borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e5e7eb'
                                }}
                            >
                                <span className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>O Escanea este Código QR</span>
                                <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm inline-block">
                                    <QRCodeSVG
                                        value={comprobanteQR.codigo || comprobanteQR.token_verificacion || `CORR-${comprobanteQR.id}`}
                                        size={180}
                                        level="M"
                                        includeMargin={true}
                                    />
                                </div>
                            </div>

                            {/* Opción 4: Enlace de Seguimiento Público (Para usuarios sin cuenta) */}
                            <div 
                                className="rounded-2xl p-3.5 text-left space-y-2 border"
                                style={{
                                    background: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f8fafc',
                                    borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0'
                                }}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: isDark ? '#ffffff' : '#374151' }}>
                                        <Link2 className="w-3.5 h-3.5 text-[#C1348B]" />
                                        Enlace de Seguimiento Público
                                    </span>
                                    <span className="text-[10px] text-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300 font-semibold px-2 py-0.5 rounded-full">
                                        No requiere cuenta
                                    </span>
                                </div>
                                <p className="text-[11px]" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                    Comparte este enlace para que cualquier persona (incluso sin cuenta) vea el proceso de entrega y comprobante:
                                </p>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        readOnly
                                        value={`${window.location.origin}/seguimiento/${encodeURIComponent(comprobanteQR.token_verificacion || "")}`}
                                        className="flex-1 px-3 py-1.5 rounded-xl text-xs font-mono outline-none select-all border"
                                        style={{
                                            background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                                            borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : '#d1d5db',
                                            color: isDark ? '#ffffff' : '#374151'
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const url = `${window.location.origin}/seguimiento/${encodeURIComponent(comprobanteQR.token_verificacion || "")}`;
                                            navigator.clipboard.writeText(url);
                                            setCopiadoPublicLink(true);
                                            setTimeout(() => setCopiadoPublicLink(false), 2000);
                                        }}
                                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-xl shadow-sm border transition-all whitespace-nowrap"
                                        style={{
                                            color: isDark ? '#ffd97c' : '#61257B',
                                            background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                                            borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#ddd6fe'
                                        }}
                                    >
                                        {copiadoPublicLink ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                                        <span>{copiadoPublicLink ? '¡Copiado!' : 'Copiar'}</span>
                                    </button>
                                </div>
                                <a
                                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                                        `Hola, te comparto el comprobante y seguimiento de la encomienda en Comarca Coworking:\n📦 Código: ${comprobanteQR.codigo || `#${comprobanteQR.id}`}\n🔐 PIN de Retiro: ${comprobanteQR.pin_seguridad || '—'}\n📱 Enlace público de seguimiento (sin necesidad de cuenta):\n${window.location.origin}/seguimiento/${encodeURIComponent(comprobanteQR.token_verificacion || "")}\n\n_Muestra este comprobante en recepción para retirar tu paquete._`
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full py-2 px-3 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-xs rounded-xl flex items-center justify-center gap-2 shadow-sm transition-all"
                                >
                                    <MessageSquare className="w-3.5 h-3.5" />
                                    Compartir Enlace por WhatsApp
                                </a>
                            </div>

                            <div 
                                className="text-left rounded-xl p-3 space-y-1.5 text-xs border"
                                style={{
                                    background: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f9fafb',
                                    borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                                    color: isDark ? '#cbd5e1' : '#4b5563'
                                }}
                            >
                                <div className="flex justify-between">
                                    <span style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>Reserva:</span>
                                    <span className="font-semibold" style={{ color: isDark ? '#ffffff' : '#374151' }}>{comprobanteQR.cita_codigo || 'Principal'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>Remitente:</span>
                                    <span className="font-semibold" style={{ color: isDark ? '#ffffff' : '#374151' }}>{comprobanteQR.remitente_nombre} {comprobanteQR.remitente_apellido}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>Recepción:</span>
                                    <span className="font-semibold" style={{ color: isDark ? '#ffffff' : '#374151' }}>{new Date(comprobanteQR.fecha_recepcion).toLocaleDateString('es-BO')}</span>
                                </div>
                            </div>
                        </div>

                        {/* Footer estático siempre visible */}
                        <div 
                            className="p-4 border-t flex-shrink-0 flex gap-3 z-10"
                            style={{
                                background: isDark ? '#150a26' : '#ffffff',
                                borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                            }}
                        >
                            <button
                                onClick={handleImprimirComprobante}
                                className="flex-1 py-3 px-4 text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition-colors"
                                style={{
                                    background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                                    color: isDark ? '#ffffff' : '#374151'
                                }}
                            >
                                <Printer className="w-4 h-4" />
                                Imprimir Comprobante
                            </button>
                            <button
                                onClick={() => setComprobanteQR(null)}
                                className="flex-1 py-3 px-4 text-xs font-bold text-white bg-gradient-to-r from-[#61257B] to-[#C1348B] hover:shadow-md rounded-xl transition-all"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Cabecera */}
            <div 
                className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 rounded-2xl border shadow-sm"
                style={{
                    background: isDark ? 'rgba(26, 11, 46, 0.75)' : '#ffffff',
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                }}
            >
                <div className="flex items-center gap-3.5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-[#61257B] to-[#C1348B] flex items-center justify-center text-white shadow-md shadow-purple-900/30">
                        <Mail className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold" style={{ color: isDark ? '#ffffff' : '#111827' }}>Mi Correspondencia y Envíos</h1>
                        <p className="text-sm" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>Revisa tus paquetes recibidos, fotografías de respaldo y códigos de retiro seguro</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setMostrarModalRegistrar(true)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#61257B] to-[#C1348B] hover:shadow-lg hover:shadow-purple-900/30 text-white text-xs font-bold rounded-xl transition-all"
                    >
                        <Plus className="w-4 h-4" />
                        Mandar / Registrar Encomienda
                    </button>
                    <button
                        onClick={loadData}
                        className="p-2.5 rounded-xl border transition-colors"
                        style={{
                            color: isDark ? '#e2e8f0' : '#4b5563',
                            background: isDark ? 'rgba(255, 255, 255, 0.06)' : '#ffffff',
                            borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'
                        }}
                        title="Recargar"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Estadísticas de Correspondencia */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div 
                    className="p-5 rounded-2xl border shadow-sm flex items-center justify-between"
                    style={{
                        background: isDark ? 'rgba(26, 11, 46, 0.75)' : '#ffffff',
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                    }}
                >
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: isDark ? '#ffd97c' : '#61257B' }}>En Recepción</span>
                        <p className="text-2xl font-bold mt-1" style={{ color: isDark ? '#ffffff' : '#111827' }}>{totalRecibidas}</p>
                    </div>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: isDark ? 'rgba(97, 37, 123, 0.3)' : '#f3e8ff', color: isDark ? '#ffd97c' : '#61257B' }}>
                        <Mail className="w-5 h-5" />
                    </div>
                </div>

                <div 
                    className="p-5 rounded-2xl border shadow-sm flex items-center justify-between"
                    style={{
                        background: isDark ? 'rgba(26, 11, 46, 0.75)' : '#ffffff',
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                    }}
                >
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-amber-500">Listo para Retiro</span>
                        <p className="text-2xl font-bold mt-1" style={{ color: isDark ? '#ffffff' : '#111827' }}>{totalNotificadas}</p>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                        <Shield className="w-5 h-5" />
                    </div>
                </div>

                <div 
                    className="p-5 rounded-2xl border shadow-sm flex items-center justify-between"
                    style={{
                        background: isDark ? 'rgba(26, 11, 46, 0.75)' : '#ffffff',
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                    }}
                >
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-emerald-500">Entregados</span>
                        <p className="text-2xl font-bold mt-1" style={{ color: isDark ? '#ffffff' : '#111827' }}>{totalEntregadas}</p>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5" />
                    </div>
                </div>
            </div>

            {/* Filtros */}
            <div 
                className="p-4 rounded-2xl border shadow-sm flex flex-col md:flex-row gap-3"
                style={{
                    background: isDark ? 'rgba(26, 11, 46, 0.75)' : '#ffffff',
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                }}
            >
                <div className="relative flex-1">
                    <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar por código de seguimiento, remitente o descripción..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="pl-10 pr-4 py-2.5 rounded-xl outline-none focus:ring-2 focus:ring-[#61257B]/30 focus:border-[#61257B] w-full text-sm border"
                        style={{
                            background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                            borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : '#d1d5db',
                            color: isDark ? '#ffffff' : '#111827'
                        }}
                    />
                </div>

                <div className="flex gap-2">
                    <select
                        value={filtroEstado}
                        onChange={(e) => setFiltroEstado(e.target.value)}
                        className="px-4 py-2.5 rounded-xl outline-none focus:ring-2 focus:ring-[#61257B]/30 focus:border-[#61257B] text-sm border"
                        style={{
                            background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                            borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : '#d1d5db',
                            color: isDark ? '#ffffff' : '#374151'
                        }}
                    >
                        <option value="todos">Todos los Estados</option>
                        <option value="recibido">En Recepción</option>
                        <option value="notificado">Listo para Retiro</option>
                        <option value="entregado">Entregado</option>
                    </select>

                    <select
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2.5 rounded-xl outline-none focus:ring-2 focus:ring-[#61257B]/30 focus:border-[#61257B] text-sm border"
                        style={{
                            background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#ffffff',
                            borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : '#d1d5db',
                            color: isDark ? '#ffffff' : '#374151'
                        }}
                        title="Registros por página"
                    >
                        <option value={5}>5 por pág.</option>
                        <option value={10}>10 por pág.</option>
                        <option value={20}>20 por pág.</option>
                    </select>
                </div>
            </div>

            {/* Lista de Paquetes */}
            {loading ? (
                <div 
                    className="rounded-2xl border p-16 text-center shadow-sm"
                    style={{
                        background: isDark ? 'rgba(26, 11, 46, 0.75)' : '#ffffff',
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                    }}
                >
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#61257B] mx-auto"></div>
                    <p className="mt-4 text-sm" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>Cargando correspondencias...</p>
                </div>
            ) : paginatedItems.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                    {paginatedItems.map((item) => (
                        <div 
                            key={item.id} 
                            className="rounded-2xl p-5 hover:shadow-md transition-all duration-200 border"
                            style={{
                                background: isDark ? 'rgba(26, 11, 46, 0.75)' : '#ffffff',
                                borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e5e7eb'
                            }}
                        >
                            <div className="flex flex-col lg:flex-row gap-5">
                                {/* Fotos de respaldo / Miniaturas */}
                                <div className="flex-shrink-0 flex lg:flex-col gap-2">
                                    {item.imagen && (
                                        <div 
                                            className="relative w-28 lg:w-36 h-28 lg:h-32 rounded-xl overflow-hidden cursor-pointer group border"
                                            style={{
                                                background: isDark ? 'rgba(15, 7, 28, 0.8)' : '#f3f4f6',
                                                borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'
                                            }}
                                            onClick={() => setVerFotoModal(item.imagen || null)}
                                            title="Click para ver foto de recepción"
                                        >
                                            <img 
                                                src={item.imagen} 
                                                alt="Foto Paquete" 
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" 
                                            />
                                            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
                                                <Eye className="w-5 h-5" />
                                            </div>
                                            <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                                                Foto Recepción
                                            </span>
                                        </div>
                                    )}

                                    {item.imagen_entrega && (
                                        <div 
                                            className="relative w-28 lg:w-36 h-28 lg:h-32 rounded-xl overflow-hidden cursor-pointer group border"
                                            style={{
                                                background: isDark ? 'rgba(16, 185, 129, 0.15)' : '#ecfdf5',
                                                borderColor: isDark ? 'rgba(16, 185, 129, 0.3)' : '#6ee7b7'
                                            }}
                                            onClick={() => setVerFotoModal(item.imagen_entrega || null)}
                                            title="Click para ver foto de entrega"
                                        >
                                            <img 
                                                src={item.imagen_entrega} 
                                                alt="Foto Entrega" 
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" 
                                            />
                                            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
                                                <Eye className="w-5 h-5" />
                                            </div>
                                            <span className="absolute bottom-1 right-1 bg-emerald-700 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                                                Foto Entrega
                                            </span>
                                        </div>
                                    )}

                                    {!item.imagen && !item.imagen_entrega && (
                                        <div 
                                            className="w-28 lg:w-36 h-28 lg:h-32 rounded-xl border border-dashed flex flex-col items-center justify-center text-gray-400"
                                            style={{
                                                background: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f9fafb',
                                                borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'
                                            }}
                                        >
                                            <ImageIcon className="w-8 h-8 mb-1 opacity-50" />
                                            <span className="text-[10px]">Sin fotos</span>
                                        </div>
                                    )}
                                </div>

                                {/* Información Principal */}
                                <div className="flex-1 space-y-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="flex flex-wrap items-center gap-2.5">
                                            <span 
                                                className="px-3 py-1 text-sm font-mono font-bold rounded-lg border flex items-center gap-1.5"
                                                style={{
                                                    background: isDark ? 'rgba(97, 37, 123, 0.25)' : '#f5f3ff',
                                                    color: isDark ? '#ffd97c' : '#61257B',
                                                    borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#ddd6fe'
                                                }}
                                            >
                                                {item.codigo || `#${item.id}`}
                                                <button
                                                    type="button"
                                                    onClick={() => handleCopiarCodigo(item.id, item.codigo || `#${item.id}`)}
                                                    className="p-1 rounded transition-colors"
                                                    style={{ color: isDark ? '#ffd97c' : '#61257B' }}
                                                    title="Copiar código"
                                                >
                                                    {copiadoCodigoId === item.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                                </button>
                                            </span>
                                            <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${getEstadoColor(item.estado)}`}>
                                                {getEstadoTexto(item.estado)}
                                            </span>
                                            {item.cita_codigo && (
                                                <span className="text-xs" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>
                                                    Reserva: <strong style={{ color: isDark ? '#ffffff' : '#4b5563' }}>{item.cita_codigo}</strong>
                                                </span>
                                            )}
                                        </div>

                                        {/* Botones de acción: Proceso, WhatsApp, Seguimiento y Comprobante */}
                                        <div className="flex flex-wrap items-center gap-2">
                                            <button
                                                onClick={() => setProcesoEnvioItem(item)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl border transition-all"
                                                style={{
                                                    color: isDark ? '#ffd97c' : '#61257B',
                                                    background: isDark ? 'rgba(97, 37, 123, 0.2)' : '#fdf4ff',
                                                    borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#f5d0fe'
                                                }}
                                                title="Ver Proceso de Envío y Fotos de Evidencia"
                                            >
                                                <Clock className="w-3.5 h-3.5" />
                                                Proceso de Envío
                                            </button>

                                            <a
                                                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                                                    `Hola, te comparto el seguimiento de la encomienda en Comarca Coworking:\n📦 Código: ${item.codigo || `#${item.id}`}\n🔐 PIN de Retiro: ${item.pin_seguridad || '—'}\n📱 Comprobante y QR: ${window.location.origin}/seguimiento/${encodeURIComponent(item.token_verificacion || "")}`
                                                )}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-all"
                                                title="Compartir por WhatsApp"
                                            >
                                                <MessageSquare className="w-3.5 h-3.5" />
                                                WhatsApp
                                            </a>

                                            <a
                                                href={`/seguimiento/${encodeURIComponent(item.token_verificacion || "")}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-xl transition-colors"
                                                style={{
                                                    color: isDark ? '#cbd5e1' : '#4b5563',
                                                    background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                                                }}
                                                title="Ver enlace público"
                                            >
                                                <ExternalLink className="w-3.5 h-3.5" />
                                                Enlace
                                            </a>

                                            {item.estado !== 'cancelado' && (
                                                <button
                                                    onClick={() => setComprobanteQR(item)}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-[#61257B] to-[#C1348B] hover:shadow-md hover:shadow-purple-900/30 rounded-xl transition-all"
                                                >
                                                    <QrCode className="w-4 h-4" />
                                                    Comprobante / QR
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <p className="text-sm font-medium" style={{ color: isDark ? '#f1f5f9' : '#1f2937' }}>
                                        {item.descripcion || 'Sin descripción especificada'}
                                    </p>

                                    {/* Mini Stepper del Proceso de Envío en la Tarjeta */}
                                    <div 
                                        className="rounded-2xl p-3.5 space-y-2 border"
                                        style={{
                                            background: isDark ? 'rgba(15, 7, 28, 0.6)' : '#f8fafc',
                                            borderColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(226, 232, 240, 0.7)'
                                        }}
                                    >
                                        <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wider" style={{ color: isDark ? '#e2e8f0' : '#374151' }}>
                                            <span className="flex items-center gap-1.5">
                                                <Clock className="w-3.5 h-3.5 text-[#C1348B]" />
                                                Proceso de la Encomienda
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setProcesoEnvioItem(item)}
                                                className="text-[11px] font-semibold hover:underline flex items-center gap-1 normal-case transition-colors"
                                                style={{ color: isDark ? '#ffd97c' : '#61257B' }}
                                            >
                                                Ver Detalle y Fotos <ArrowRight className="w-3 h-3" />
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-3 gap-2 text-center pt-1 relative">
                                            {/* Paso 1: Registrado */}
                                            <div className="flex flex-col items-center">
                                                <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold shadow-sm">
                                                    ✓
                                                </div>
                                                <span className="text-[11px] font-bold mt-1" style={{ color: isDark ? '#f1f5f9' : '#1f2937' }}>1. Registrado</span>
                                                <span className="text-[10px] truncate max-w-[110px]" style={{ color: isDark ? '#94a3b8' : '#6b7280' }} title={item.remitente_nombre}>
                                                    De: {item.remitente_nombre}
                                                </span>
                                            </div>

                                            {/* Paso 2: En Recepción */}
                                            <div className="flex flex-col items-center">
                                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shadow-sm ${
                                                    item.estado === 'entregado'
                                                        ? 'bg-emerald-500 text-white'
                                                        : item.estado === 'notificado' || item.imagen
                                                        ? 'bg-gradient-to-r from-[#61257B] to-[#C1348B] text-white ring-2 ring-purple-400 ring-offset-1 animate-pulse'
                                                        : isDark ? 'bg-purple-950/60 text-purple-300' : 'bg-purple-100 text-purple-800'
                                                }`}>
                                                    {item.estado === 'entregado' ? '✓' : '2'}
                                                </div>
                                                <span className="text-[11px] font-bold mt-1" style={{ color: isDark ? '#f1f5f9' : '#1f2937' }}>2. En Recepción</span>
                                                <span className="text-[10px] truncate max-w-[120px]" style={{ color: isDark ? '#94a3b8' : '#6b7280' }} title={item.usuario_recibe_nombre ? `Recepcionista: ${item.usuario_recibe_nombre}` : (item.imagen ? '📸 Con Foto Custodia' : 'En Custodia')}>
                                                    {item.usuario_recibe_nombre ? `Por: ${item.usuario_recibe_nombre}` : (item.imagen ? '📸 Con Foto' : 'En Custodia')}
                                                </span>
                                            </div>

                                            {/* Paso 3: Entregado */}
                                            <div className="flex flex-col items-center">
                                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shadow-sm ${
                                                    item.estado === 'entregado'
                                                        ? 'bg-emerald-500 text-white'
                                                        : isDark ? 'bg-gray-800 text-gray-500' : 'bg-gray-200 text-gray-400'
                                                }`}>
                                                    {item.estado === 'entregado' ? '✓' : '3'}
                                                </div>
                                                <span className="text-[11px] font-bold mt-1" style={{ color: isDark ? '#f1f5f9' : '#1f2937' }}>3. Entregado</span>
                                                <span className="text-[10px] truncate max-w-[110px]" style={{ color: isDark ? '#94a3b8' : '#6b7280' }} title={item.receptor_nombre || ''}>
                                                    {item.estado === 'entregado' ? (item.receptor_nombre || 'Retirado') : 'Pendiente Retiro'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div 
                                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 text-xs border-t"
                                        style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6' }}
                                    >
                                        <div>
                                            <span className="block mb-0.5" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>Remitente:</span>
                                            <p className="font-semibold" style={{ color: isDark ? '#ffffff' : '#374151' }}>{item.remitente_nombre} {item.remitente_apellido}</p>
                                            <p style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>{item.remitente_ci_nit}</p>
                                        </div>

                                        <div>
                                            <span className="block mb-0.5" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>Fecha de Recepción:</span>
                                            <p className="font-semibold" style={{ color: isDark ? '#ffffff' : '#374151' }}>{formatDateTime(item.fecha_recepcion)}</p>
                                        </div>

                                        <div>
                                            <span className="block mb-0.5" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>Recepcionado por:</span>
                                            <p className="font-semibold flex items-center gap-1" style={{ color: isDark ? '#ffffff' : '#1f2937' }}>
                                                <UserCheck className="w-3.5 h-3.5 text-[#C1348B]" />
                                                <span className="truncate" title={item.usuario_recibe_nombre || 'Recepción Comarca'}>
                                                    {item.usuario_recibe_nombre || 'Recepción Comarca'}
                                                </span>
                                            </p>
                                        </div>

                                        <div>
                                            <span className="block mb-0.5" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>Estado de Entrega:</span>
                                            {item.estado === 'entregado' ? (
                                                <div>
                                                    <p className="font-semibold text-emerald-500 flex items-center gap-1">
                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                        Retirado por: {item.receptor_nombre || 'Cliente'}
                                                    </p>
                                                    <p style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>{formatDateTime(item.fecha_entrega)}</p>
                                                </div>
                                            ) : (
                                                <p className="font-medium text-amber-500">Pendiente de Retiro en Recepción</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* PIN Secreto */}
                                    {item.pin_seguridad && item.estado !== 'entregado' && item.estado !== 'cancelado' && (
                                        <div 
                                            className="mt-3 p-3 rounded-xl border flex items-center justify-between"
                                            style={{
                                                background: isDark ? 'linear-gradient(to right, rgba(97, 37, 123, 0.25), rgba(193, 52, 139, 0.15))' : 'linear-gradient(to right, #faf5ff, #fdf4ff)',
                                                borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#f0abfc'
                                            }}
                                        >
                                            <div className="flex items-center gap-2.5">
                                                <div 
                                                    className="p-1.5 rounded-lg"
                                                    style={{
                                                        background: isDark ? 'rgba(193, 52, 139, 0.25)' : 'rgba(97, 37, 123, 0.1)',
                                                        color: isDark ? '#ffd97c' : '#61257B'
                                                    }}
                                                >
                                                    <Shield className="w-4 h-4" />
                                                </div>
                                                <div>
                                                    <span className="text-[11px] font-medium block" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>Tu PIN Secreto de Retiro:</span>
                                                    <span className="text-lg font-mono font-extrabold tracking-widest" style={{ color: isDark ? '#ffd97c' : '#61257B' }}>
                                                        {item.pin_seguridad}
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleCopiarPin(item.id, item.pin_seguridad || '')}
                                                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg shadow-sm border transition-colors"
                                                style={{
                                                    color: isDark ? '#ffd97c' : '#61257B',
                                                    background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                                                    borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#e5e7eb'
                                                }}
                                            >
                                                {copiadoPinId === item.id ? (
                                                    <>
                                                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                                                        <span>¡Copiado!</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy className="w-3.5 h-3.5" />
                                                        <span>Copiar PIN</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Paginación */}
                    {correspondenciasFiltradas.length > 0 && (
                        <div 
                            className="rounded-2xl border shadow-sm overflow-hidden mt-4"
                            style={{
                                background: isDark ? 'rgba(26, 11, 46, 0.75)' : '#ffffff',
                                borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                            }}
                        >
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={(page) => setCurrentPage(page)}
                                itemsPerPage={itemsPerPage}
                                totalItems={correspondenciasFiltradas.length}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <div 
                    className="rounded-2xl border p-16 text-center shadow-sm"
                    style={{
                        background: isDark ? 'rgba(26, 11, 46, 0.75)' : '#ffffff',
                        borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                    }}
                >
                    <div 
                        className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
                        style={{ background: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6' }}
                    >
                        <Mail className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-base font-semibold mb-1" style={{ color: isDark ? '#ffffff' : '#1f2937' }}>
                        No hay correspondencias registradas
                    </h3>
                    <p className="text-sm max-w-md mx-auto" style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>
                        {busqueda || filtroEstado !== 'todos'
                            ? 'No se encontraron paquetes con los filtros aplicados'
                            : 'No tienes paquetes o documentos pendientes de retiro en recepción'}
                    </p>
                </div>
            )}

            {/* Modal de Registrar Encomienda para el Cliente */}
            <RegistrarEncomiendaClienteModal
                isOpen={mostrarModalRegistrar}
                onClose={() => setMostrarModalRegistrar(false)}
                onSave={loadData}
            />

            {/* Modal de Proceso de Envío y Evidencias para el Cliente */}
            {procesoEnvioItem && createPortal(
                <div 
                    className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-3 sm:p-4 md:p-6 backdrop-blur-md font-['DM_Sans',sans-serif] overflow-y-auto"
                    onClick={() => setProcesoEnvioItem(null)}
                >
                    <div 
                        className="rounded-3xl shadow-2xl max-w-lg w-full max-h-[92vh] flex flex-col overflow-hidden animate-scale-up my-auto border"
                        style={{
                            background: isDark ? '#150a26' : '#ffffff',
                            borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#e5e7eb',
                            color: isDark ? '#ffffff' : '#111827'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header estático */}
                        <div 
                            className="sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between flex-shrink-0"
                            style={{
                                background: isDark ? '#150a26' : '#ffffff',
                                borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                            }}
                        >
                            <div className="flex items-center gap-2.5">
                                <div className="p-2 bg-gradient-to-tr from-[#61257B] to-[#C1348B] text-white rounded-xl shadow-sm shadow-purple-900/30">
                                    <Clock className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold" style={{ color: isDark ? '#ffffff' : '#1f2937' }}>
                                        Proceso de Envío: {procesoEnvioItem.codigo || `#${procesoEnvioItem.id}`}
                                    </h3>
                                    <p className="text-xs" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                        Para retirar por: <strong style={{ color: isDark ? '#ffd97c' : '#61257B' }}>{procesoEnvioItem.destinatario_nombre || 'Cliente'}</strong>
                                    </p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setProcesoEnvioItem(null)} 
                                className="p-1.5 rounded-lg transition-colors"
                                style={{
                                    color: isDark ? '#cbd5e1' : '#6b7280',
                                    background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                                }}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Timeline Scrolleable */}
                        <div className="p-6 space-y-6 overflow-y-auto flex-1 custom-scroll">
                            <div className="relative pl-6 space-y-6 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-300 dark:before:bg-gray-700">
                                {/* Paso 1: Registrado */}
                                <div className="relative">
                                    <div className="absolute -left-6 top-0 w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold shadow">
                                        ✓
                                    </div>
                                    <div className="text-xs space-y-0.5">
                                        <span className="font-bold block" style={{ color: isDark ? '#ffffff' : '#111827' }}>1. Encomienda Registrada</span>
                                        <p style={{ color: isDark ? '#cbd5e1' : '#4b5563' }}>
                                            Dejada por: <strong style={{ color: isDark ? '#ffffff' : '#1f2937' }}>{procesoEnvioItem.remitente_nombre} {procesoEnvioItem.remitente_apellido || ''}</strong>
                                        </p>
                                        {procesoEnvioItem.descripcion && (
                                            <p className="text-[11px]" style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>
                                                Contenido: {procesoEnvioItem.descripcion}
                                            </p>
                                        )}
                                        <span className="text-[10px] block" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>
                                            Fecha: {formatDateTime(procesoEnvioItem.fecha_creacion || procesoEnvioItem.fecha_recepcion)}
                                        </span>
                                    </div>
                                </div>

                                {/* Paso 2: En Custodia en Recepción */}
                                <div className="relative">
                                    <div className={`absolute -left-6 top-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shadow ${
                                        procesoEnvioItem.estado !== 'cancelado' ? 'bg-gradient-to-r from-[#61257B] to-[#C1348B] text-white' : 'bg-gray-300 text-gray-500'
                                    }`}>
                                        {procesoEnvioItem.estado === 'entregado' ? '✓' : '2'}
                                    </div>
                                    <div className="text-xs space-y-2">
                                        <div>
                                            <span className="font-bold block" style={{ color: isDark ? '#ffffff' : '#111827' }}>2. En Custodia en Recepción Comarca</span>
                                            <p className="mt-0.5" style={{ color: isDark ? '#cbd5e1' : '#4b5563' }}>
                                                Guardado bajo custodia en recepción. PIN de retiro: <strong className="font-mono" style={{ color: isDark ? '#ffd97c' : '#61257B' }}>{procesoEnvioItem.pin_seguridad || 'N/A'}</strong>
                                            </p>
                                            <div 
                                                className="mt-1.5 p-2 rounded-xl flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] border"
                                                style={{
                                                    background: isDark ? 'rgba(97, 37, 123, 0.2)' : 'rgba(245, 243, 255, 0.8)',
                                                    borderColor: isDark ? 'rgba(193, 52, 139, 0.3)' : '#ddd6fe'
                                                }}
                                            >
                                                <span className="flex items-center gap-1" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>
                                                    <UserCheck className="w-3.5 h-3.5 text-[#C1348B]" />
                                                    Recepcionado por: <strong style={{ color: isDark ? '#ffffff' : '#111827' }}>{procesoEnvioItem.usuario_recibe_nombre || 'Personal de Recepción'}</strong>
                                                </span>
                                                <span style={{ color: isDark ? '#64748b' : '#d1d5db' }}>•</span>
                                                <span style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>
                                                    Fecha Recepción: {formatDateTime(procesoEnvioItem.fecha_recepcion)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Evidencia Fotográfica de Recepción */}
                                        {procesoEnvioItem.imagen ? (
                                            <div 
                                                className="p-2.5 rounded-xl space-y-1.5 border"
                                                style={{
                                                    background: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f9fafb',
                                                    borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e5e7eb'
                                                }}
                                            >
                                                <span className="text-[10px] font-bold flex items-center gap-1 uppercase tracking-wider" style={{ color: isDark ? '#ffd97c' : '#61257B' }}>
                                                    <Camera className="w-3.5 h-3.5 text-[#C1348B]" />
                                                    Fotografía de Evidencia (Recepción):
                                                </span>
                                                <div 
                                                    className="relative h-44 rounded-lg overflow-hidden bg-white cursor-pointer group border"
                                                    onClick={() => setVerFotoModal(procesoEnvioItem.imagen || null)}
                                                >
                                                    <img 
                                                        src={procesoEnvioItem.imagen} 
                                                        alt="Evidencia Recepción" 
                                                        className="w-full h-full object-contain group-hover:scale-105 transition-transform" 
                                                    />
                                                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs font-semibold gap-1 transition-opacity">
                                                        <Eye className="w-4 h-4" />
                                                        Click para Ampliar Foto
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-[11px] italic block" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>
                                                (Evidencia fotográfica en espera de recepción física)
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Paso 3: Entrega al Destinatario */}
                                <div className="relative">
                                    <div className={`absolute -left-6 top-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shadow ${
                                        procesoEnvioItem.estado === 'entregado' 
                                            ? 'bg-emerald-500 text-white' 
                                            : isDark ? 'bg-gray-800 text-gray-500' : 'bg-gray-200 text-gray-500'
                                    }`}>
                                        {procesoEnvioItem.estado === 'entregado' ? '✓' : '3'}
                                    </div>
                                    <div className="text-xs space-y-2">
                                        <div>
                                            <span className="font-bold block" style={{ color: isDark ? '#ffffff' : '#111827' }}>3. Entrega al Destinatario</span>
                                            {procesoEnvioItem.estado === 'entregado' ? (
                                                <div className="mt-0.5 space-y-1">
                                                    <p className="text-emerald-500 font-bold flex items-center gap-1">
                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                        Entregado a: {procesoEnvioItem.receptor_nombre} {procesoEnvioItem.receptor_apellido || ''}
                                                    </p>
                                                    {procesoEnvioItem.usuario_entrega_nombre && (
                                                        <p className="text-[11px] flex items-center gap-1" style={{ color: isDark ? '#cbd5e1' : '#374151' }}>
                                                            <UserCheck className="w-3.5 h-3.5 text-emerald-500" />
                                                            Entregado por personal: <strong style={{ color: isDark ? '#ffffff' : '#111827' }}>{procesoEnvioItem.usuario_entrega_nombre}</strong>
                                                        </p>
                                                    )}
                                                    {procesoEnvioItem.receptor_ci_nit && (
                                                        <span className="text-[11px] block" style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>
                                                            CI Receptor: {procesoEnvioItem.receptor_ci_nit}
                                                        </span>
                                                    )}
                                                    <span className="text-[10px] block" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>
                                                        Fecha de Entrega: {formatDateTime(procesoEnvioItem.fecha_entrega)}
                                                    </span>
                                                </div>
                                            ) : (
                                                <p className="text-amber-500 font-medium mt-0.5">
                                                    Pendiente de retiro con PIN en recepción. Destinatario: <strong style={{ color: isDark ? '#ffd97c' : '#61257B' }}>{procesoEnvioItem.destinatario_nombre || 'Cliente'}</strong>
                                                </p>
                                            )}
                                        </div>

                                        {/* Evidencia Fotográfica de Entrega */}
                                        {procesoEnvioItem.estado === 'entregado' && procesoEnvioItem.imagen_entrega && (
                                            <div 
                                                className="p-2.5 rounded-xl space-y-1.5 border"
                                                style={{
                                                    background: isDark ? 'rgba(16, 185, 129, 0.15)' : '#ecfdf5',
                                                    borderColor: isDark ? 'rgba(16, 185, 129, 0.3)' : '#a7f3d0'
                                                }}
                                            >
                                                <span className="text-[10px] font-bold text-emerald-500 flex items-center gap-1 uppercase tracking-wider">
                                                    <Camera className="w-3.5 h-3.5 text-emerald-500" />
                                                    Fotografía de Respaldo de Entrega:
                                                </span>
                                                <div 
                                                    className="relative h-44 rounded-lg overflow-hidden bg-white cursor-pointer group border border-emerald-200"
                                                    onClick={() => setVerFotoModal(procesoEnvioItem.imagen_entrega || null)}
                                                >
                                                    <img 
                                                        src={procesoEnvioItem.imagen_entrega} 
                                                        alt="Evidencia Entrega" 
                                                        className="w-full h-full object-contain group-hover:scale-105 transition-transform" 
                                                    />
                                                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs font-semibold gap-1 transition-opacity">
                                                        <Eye className="w-4 h-4" />
                                                        Click para Ampliar Foto
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer estático */}
                        <div 
                            className="p-4 border-t flex-shrink-0 flex gap-2 z-10"
                            style={{
                                background: isDark ? '#150a26' : '#ffffff',
                                borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                            }}
                        >
                            <a
                                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                                    `Hola, te comparto el seguimiento de la encomienda en Comarca Coworking:\n📦 Código: ${procesoEnvioItem.codigo || `#${procesoEnvioItem.id}`}\n🔐 PIN de Retiro: ${procesoEnvioItem.pin_seguridad || '—'}\n📱 Comprobante y QR: ${window.location.origin}/seguimiento/${encodeURIComponent(procesoEnvioItem.token_verificacion || "")}`
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 py-2.5 px-3 bg-[#25D366] hover:bg-[#20bd5a] text-white text-xs font-bold rounded-xl flex items-center justify-center gap-2 shadow-sm transition-all"
                            >
                                <MessageSquare className="w-4 h-4" />
                                Compartir por WhatsApp
                            </a>
                            <button
                                onClick={() => setProcesoEnvioItem(null)}
                                className="py-2.5 px-5 text-xs font-semibold rounded-xl transition-colors"
                                style={{
                                    background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                                    color: isDark ? '#ffffff' : '#374151'
                                }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default MiCorrespondenciaPage;
